import { useMemo } from 'react';

import { Cluster } from '@cast/types';

import { CloudVolume } from '@cast/types-stop';
import { NodeWithDependencies } from 'features/cluster/nodes/types';

import { StorageDiskCharts } from '../../_components/StorageDiskCharts';
import { useCloudVolumeMetricsQuery } from 'hooks/queries/nodes/useCloudVolumeMetricsQuery';
import { ManagedDiskCurrentUsage } from './ManagedDiskCurrentUsage';

import { ManagedDiskMetaCard } from './ManagedDiskMetaCard';
import { useNodeStorageTimeRangeSelection } from '../../_hooks/useNodeStorageTimeRangeSelection';
import { resolveMetricsStep } from '../../_util';
import { Typography, Stack } from '@cast/design-system';

const SYNC_ID = 'managed-disk-volume-charts';

type ManagedDiskTabContentProps = {
  node?: NodeWithDependencies;
  cluster: Cluster;
  disk: CloudVolume;
  isCloudDisksEnabled?: boolean;
};

export const ManagedDiskTabContent = ({
  node,
  cluster,
  disk,
  isCloudDisksEnabled,
}: ManagedDiskTabContentProps) => {
  const { getRange, key, timeZone } = useNodeStorageTimeRangeSelection();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const { from, to } = useMemo(() => getRange(), [key]);

  const { series, summary, isLoading, error, refetch } =
    useCloudVolumeMetricsQuery({
      clusterId: cluster.id,
      nodeName: node?.name,
      volumeId: disk.volumeId,
      startTime: from,
      endTime: to,
      step: resolveMetricsStep(from, to),
    });

  return (
    <Stack gap={16}>
      <ManagedDiskMetaCard disk={disk} />

      <ManagedDiskCurrentUsage
        node={node}
        cluster={cluster}
        disk={disk}
        isCloudDisksEnabled={isCloudDisksEnabled}
      />

      <Stack gap={8}>
        <Typography variant="A1" color="textSecondary">
          Usage over time
        </Typography>
        <StorageDiskCharts
          series={series}
          summary={{
            throughputBytes: summary?.throughputBytes?.avg,
            iops: summary?.iops?.avg,
            provisionedIops: summary?.provisionedIops?.avg,
            provisionedThroughputBytes:
              summary?.provisionedThroughputBytes?.avg,
          }}
          isLoading={isLoading}
          error={error}
          refetch={refetch}
          syncId={SYNC_ID}
          timeZone={timeZone}
          isNodeReady={!!node}
          showCapacity={false}
          isCloudDisksEnabled={isCloudDisksEnabled}
        />
      </Stack>
    </Stack>
  );
};
